/**
 * Format a number as currency (handles Stripe amounts in cents)
 */
export const formatCurrency = (
	amount: number,
	currency: string = 'USD',
	fromCents: boolean = true
): string => {
	const value = fromCents ? amount / 100 : amount;
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency,
		minimumFractionDigits: value % 1 === 0 ? 0 : 2,
		maximumFractionDigits: 2,
	}).format(value);
};

/**
 * Format a number with appropriate thousands separators
 */
export const formatNumber = (num: number): string => {
	return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Format a decimal as a percentage
 */
export const formatPercentage = (
	decimal: number,
	decimalPlaces: number = 1
): string => {
	return `${decimal.toFixed(decimalPlaces)}%`;
};

/**
 * Format a date for display
 */
export const formatDate = (date: string | Date): string => {
	const dateObj = typeof date === 'string' ? new Date(date) : date;
	return dateObj.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
};

/**
 * Format a date and time for display
 */
export const formatDateTime = (date: string | Date): string => {
	const dateObj = typeof date === 'string' ? new Date(date) : date;
	return dateObj.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

/**
 * Format time duration in a human readable format
 */
export const formatDuration = (milliseconds: number): string => {
	const seconds = Math.floor(milliseconds / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (days > 0) return `${days}d ${hours % 24}h`;
	if (hours > 0) return `${hours}h ${minutes % 60}m`;
	if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
	return `${seconds}s`;
};

/**
 * Shorten large numbers with K, M, B suffixes
 */
export const formatCompactNumber = (num: number): string => {
	if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
	if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
	if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
	return num.toString();
};

/**
 * Format file size in bytes to human readable format
 */
export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Capitalize first letter of each word
 */
export const formatTitleCase = (str: string): string => {
	return str.replace(
		/\w\S*/g,
		(txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
	);
};

/**
 * Format payment method type for display
 */
export const formatPaymentMethod = (method: string): string => {
	switch (method.toLowerCase()) {
		case 'card':
			return 'Credit/Debit Card';
		case 'bank_transfer':
			return 'Bank Transfer';
		case 'sepa_debit':
			return 'SEPA Direct Debit';
		case 'ideal':
			return 'iDEAL';
		case 'klarna':
			return 'Klarna';
		case 'afterpay_clearpay':
			return 'Afterpay/Clearpay';
		case 'alipay':
			return 'Alipay';
		case 'wechat_pay':
			return 'WeChat Pay';
		default:
			return formatTitleCase(method.replace(/_/g, ' '));
	}
};

/**
 * Format card brand for display
 */
export const formatCardBrand = (brand: string): string => {
	switch (brand.toLowerCase()) {
		case 'amex':
			return 'American Express';
		case 'diners':
			return 'Diners Club';
		case 'discover':
			return 'Discover';
		case 'jcb':
			return 'JCB';
		case 'mastercard':
			return 'Mastercard';
		case 'unionpay':
			return 'UnionPay';
		case 'visa':
			return 'Visa';
		default:
			return formatTitleCase(brand);
	}
};
